import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { APP_CONSTANTS } from '@constants/app.constants';
import { CustomBookingMessageTemplateDbModel } from '@data-models/templates-db.model';
import { environment } from '@env/environment';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { LoggerService } from '@services/util-service/logger.service';

/** Identifies the structured-data block so it can be found and overwritten rather than duplicated. */
const LOCAL_BUSINESS_LD_ID = 'calio-ld-localbusiness';

@Injectable({
  providedIn: 'root'
})
export class DomManipulationService {

  isBrowser = false;
  renderer: Renderer2;
  readonly #webcomponentWidgetUrl = environment.webcomponentWidgetUrl;
  readonly #defaultLanguage = APP_CONSTANTS.DEFAULT_LANG;

  constructor(
    @Inject(PLATFORM_ID) private platformId,
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private calioTranslationPipe: CalioTranslationPipe,
    private meta: Meta,
    private title: Title,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.isBrowser = isPlatformBrowser(this.platformId);

  }

  addClassToBodyTag(className: string) {
    this.renderer.addClass(this.document.body, className);
  }

  removeClassFromBodyTag(className: string) {
    this.renderer.removeClass(this.document.body, className);
  }

  setStyleToBodyTag(styleKey: string, styleValue: string) {
    this.renderer.setStyle(this.document.body, styleKey, styleValue);
  }

  updateCssInParticularClass(className: string, styleKey: string, styleValue: string) {
    const element = this.document.querySelector(className);
    if (element) {
      this.renderer.setStyle(element, styleKey, styleValue);
    }
  }

  updateCssVariable(variableName: string, value: string): void {
    if (this.document?.documentElement) {
      this.document.documentElement.style.setProperty(variableName, value);
    }
  }

  appendWidgetWebComponentDependencies(): void {
    try {
      // Set webcomponent hosted site domain as preconncet
      const preconnectLink = this.document.createElement('link');
      preconnectLink.rel = 'preconnect';
      preconnectLink.href = this.#webcomponentWidgetUrl;
      this.document.head.appendChild(preconnectLink);

      // Load html-imports.js
      const htmlImportsLink = this.document.createElement('script');
      htmlImportsLink.type = 'text/javascript';
      htmlImportsLink.src = `${this.#webcomponentWidgetUrl}/html-imports.js`;
      htmlImportsLink.setAttribute('fetchpriority', 'high');
      this.document.head.appendChild(htmlImportsLink);

      // Link booking page
      const bookingLink = this.document.createElement('link');
      bookingLink.rel = 'import';
      bookingLink.href = `${this.#webcomponentWidgetUrl}/booking.html`;
      bookingLink.setAttribute('fetchpriority', 'high');
      this.document.head.appendChild(bookingLink);
    } catch (error) {
      LoggerService.error(error);
    }
  }

  setAuthorMeta(author: string, siteName: string): void {
    this.meta.addTag({
      name: 'author',
      property: 'author',
      content: author
    });
    this.meta.addTag({
      name: 'og:site_name',
      property: 'og:site_name',
      content: siteName
    });
    this.meta.addTag({
      name: 'apple-mobile-web-app-title',
      property: 'apple-mobile-web-app-title',
      content: siteName
    });
    this.meta.addTag({
      name: 'application-name',
      property: 'application-name',
      content: siteName
    });
  }

  /**
   * Applies a title straight from a string.
   *
   * Kept separate from the template-driven overload so the resolver can lay down a
   * partner-derived title the moment the partner is known, before the (independently
   * failing) template call is even attempted.
   *
   * `updateTag` — never `addTag` — because this now runs twice per render (fallback,
   * then the template override). `addTag` only reuses an element when *every*
   * attribute including `content` already matches, so a differing override would
   * append a second og:title rather than replace the first.
   */
  setDocumentTitleText(title: string): void {
    if (!title) {
      return;
    }
    this.title.setTitle(title);
    this.meta.updateTag({
      name: 'og:title',
      property: 'og:title',
      content: title
    });
  }

  setDocumentDescriptionText(description: string): void {
    if (!description) {
      return;
    }
    this.meta.updateTag({
      name: 'description',
      property: 'description',
      content: description
    });
    this.meta.updateTag({
      name: 'og:description',
      property: 'og:description',
      content: description
    });
  }

  /**
   * Keeps a URL out of the index without hiding it from visitors — used for booking
   * slugs that resolve to nothing bookable (unknown, blocked, or booking-disabled
   * accounts). Those pages all render the same shell copy, and letting search engines
   * index them is what builds the pool of identical titles Google draws replacement
   * titles from.
   */
  setRobotsNoIndex(): void {
    this.meta.updateTag({
      name: 'robots',
      content: 'noindex, follow'
    });
  }

  setDocumentTitle(template: CustomBookingMessageTemplateDbModel, partnerName?: string): void {
    this.setDocumentTitleText(
      this.replaceTemplatePlaceholders(
        this.calioTranslationPipe.transform(template, 'template_content', this.#defaultLanguage),
        partnerName
      )
    );
  }

  setDocumentDescription(template: CustomBookingMessageTemplateDbModel, partnerName?: string): void {
    this.setDocumentDescriptionText(
      this.replaceTemplatePlaceholders(
        this.calioTranslationPipe.transform(template, 'template_content', this.#defaultLanguage),
        partnerName
      )
    );
  }

  private replaceTemplatePlaceholders(text: string, partnerName?: string): string {
    if (text && partnerName) {
      return text.replace(/\[PARTNER\]/g, partnerName);
    }
    return text;
  }

  setDocumentFavicon(favicon: string): void {
    const faviconUrl = `${favicon}`;
    const favIcon = this.document.querySelector('#favicon') as HTMLLinkElement;
    const favIconShortcutIcon = this.document.querySelector('#favicon_shortcut_icon') as HTMLLinkElement;
    const favIconTouchIcon = this.document.querySelector('#apple_touch_icon') as HTMLLinkElement;
    const imageSrcIcon = this.document.querySelector('#image_src') as HTMLLinkElement;

    favIcon.href = faviconUrl;
    favIcon.setAttribute('fetchpriority', 'high');

    favIconShortcutIcon.href = faviconUrl;
    favIconShortcutIcon.setAttribute('fetchpriority', 'high');

    favIconTouchIcon.href = faviconUrl;
    favIconTouchIcon.setAttribute('fetchpriority', 'high');

    imageSrcIcon.href = faviconUrl;
    imageSrcIcon.setAttribute('fetchpriority', 'high');
  }

  setOgImageMeta(image: string, altText: string): void {
    this.meta.addTag({
      name: 'og:image',
      property: 'og:image',
      content: image
    });
    this.meta.addTag({
      name: 'og:image:alt',
      property: 'og:image:alt',
      content: altText
    });
  }

  setOgURL(url: string): void {
    // Reuse an existing canonical instead of appending. The resolver runs again on the
    // client after hydration, and a blind appendChild left the rendered document with
    // two <link rel="canonical"> elements — which search engines treat as conflicting
    // and ignore outright.
    let bookingLink = this.document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!bookingLink) {
      bookingLink = this.document.createElement('link');
      bookingLink.rel = 'canonical';
      this.document.head.appendChild(bookingLink);
    }
    bookingLink.href = url;

    this.meta.updateTag({
      name: 'og:url',
      property: 'og:url',
      content: url
    });
  }

  /**
   * Publishes the partner as a LocalBusiness in structured data.
   *
   * The page carries very little text a machine can interpret, which is part of why search
   * engines have treated these pages as interchangeable. This states plainly whose page it is,
   * in a form that does not depend on prose.
   *
   * Written the same way as the canonical above — find the element, then overwrite it — because
   * the resolver runs a second time on the client after hydration, and appending blindly is
   * exactly what left the document with duplicate canonical and hreflang tags before.
   */
  setLocalBusinessJsonLd(payload: Record<string, unknown>): void {
    let script = this.document.head.querySelector(`script#${LOCAL_BUSINESS_LD_ID}`) as HTMLScriptElement | null;
    if (!script) {
      script = this.document.createElement('script');
      script.id = LOCAL_BUSINESS_LD_ID;
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }

    // A <script> is a raw-text element: the serialiser does not escape its contents, so a
    // literal "</script>" arriving in partner data would close the block early and the rest
    // of the JSON would be parsed as markup. Escaping every "<" removes that possibility
    // without changing what a JSON parser reads back.
    script.textContent = JSON.stringify(payload).replace(/</g, '\\u003c');
  }

  /**
   * Removes the structured data again.
   *
   * Needed because the document survives a client-side route change: a page that must not be
   * indexed would otherwise keep advertising the entity it inherited from the previous route.
   */
  removeLocalBusinessJsonLd(): void {
    this.document.head.querySelector(`script#${LOCAL_BUSINESS_LD_ID}`)?.remove();
  }

  /**
   * Declares the language the document is actually rendered in.
   *
   * Every brand's index.html hardcodes `lang="en"`, and nothing ever updated it — so a page
   * rendered in de_CH still told crawlers, screen readers and translation tools it was
   * English. `lang` takes the BCP-47 form (`de-CH`); og:locale takes the underscore form
   * (`de_CH`), which is the shape our locales already have.
   */
  setDocumentLanguage(locale: string): void {
    if (!locale) {
      return;
    }

    this.document.documentElement?.setAttribute('lang', this.#toHtmlLang(locale));

    this.meta.updateTag({
      name: 'og:locale',
      property: 'og:locale',
      content: locale
    });
  }

  /**
   * Publishes the other languages this partner's page is available in.
   *
   * Language is chosen per request from `?lang=`, so the alternates are the same URL with
   * that parameter set — and x-default is the URL without it, which is what a visitor with
   * no preference gets. Only the languages the partner actually serves are listed; naming a
   * language the partner has no translations for would send crawlers to a page rendered in
   * a different one.
   *
   * Rebuilt on every call rather than appended: the resolver runs a second time on the
   * client after hydration, and appending is what previously left the document with two
   * conflicting canonical tags.
   */
  setLanguageAlternates(pageUrl: string, locales: string[]): void {
    this.clearLanguageAlternates();

    const distinctLocales = Array.from(new Set((locales || []).filter(Boolean)));
    // One language is not a choice — hreflang would describe a set of one and say nothing.
    if (distinctLocales.length < 2) {
      return;
    }

    let canonical: URL;
    try {
      canonical = new URL(pageUrl);
    } catch (error) {
      LoggerService.error(error);
      return;
    }

    // An internal link is issued to one recipient and carries their token. It is not a
    // public address of this page in another language, and its token has no business
    // being written into a <link> tag.
    //
    // `q` counts as one of these. It is a base64 envelope the resolver decodes back into
    // ordinary parameters — `internal` and `token` among them — so reading the raw query
    // alone would wave through exactly the links this guard exists to catch. A prefilled
    // link is a personalised variant of the page in any case, not a translation of it.
    if (
      canonical.searchParams.has('token') ||
      canonical.searchParams.has('q') ||
      canonical.searchParams.get('internal') === 'true'
    ) {
      return;
    }

    const alternateUrl = (locale?: string): string => {
      const url = new URL(canonical.toString());
      // `language` is accepted as an alias of `lang`; leaving it in place would let it
      // override the locale this alternate is meant to name.
      url.searchParams.delete('language');
      locale ? url.searchParams.set('lang', locale) : url.searchParams.delete('lang');
      return url.toString();
    };

    const emitted = new Set<string>();
    distinctLocales.forEach(locale => {
      const hreflang = this.#toHreflang(locale);
      if (emitted.has(hreflang)) {
        return;
      }
      emitted.add(hreflang);
      this.#appendAlternate(hreflang, alternateUrl(locale));
    });
    this.#appendAlternate('x-default', alternateUrl());
  }

  /**
   * Drops every hreflang alternate from the document.
   *
   * Needed on its own for a page we have just declared non-indexable: the alternates were
   * written for whichever page the document last rendered, and on a client-side navigation
   * the document is not thrown away between routes, so they would survive and describe the
   * wrong URL. Deliberately NOT called for a transient failure — there the page we already
   * rendered is still the right one, and deleting correct tags because an API call blipped
   * would be a downgrade.
   */
  clearLanguageAlternates(): void {
    Array.from(this.document.head.querySelectorAll('link[rel="alternate"][hreflang]'))
      .forEach(link => link.remove());
  }

  #appendAlternate(hreflang: string, href: string): void {
    const link = this.document.createElement('link');
    link.rel = 'alternate';
    link.setAttribute('hreflang', hreflang);
    link.href = href;
    this.document.head.appendChild(link);
  }

  #toHtmlLang(locale: string): string {
    return locale.replace('_', '-');
  }

  /**
   * hreflang value for a locale — the language subtag only.
   *
   * `lang` and `hreflang` answer different questions. `lang="de-CH"` describes the content
   * ("this text is Swiss German") and is true. `hreflang="de-CH"` is a *targeting* claim
   * ("show this to German speakers **in Switzerland**"), which would be a lie for the
   * German and Austrian partners on this platform: we serve one German translation, not a
   * Swiss one and a German one. `hreflang="de"` says only what is true — this is the
   * German version — and reaches every German speaker rather than a fraction of them.
   */
  #toHreflang(locale: string): string {
    return locale.split('_')[0];
  }

  setThemeColor(hex: string): void {
    hex = hex || '#3a4a66';
    this.meta.addTag({
      name: 'theme-color',
      property: 'theme-color',
      content: hex
    });
  }

  applyCssOverwrite(code: string): void {
    if (!this.isBrowser) {
      return;
    }
    const style = this.document.createElement('style');
    style.setAttribute('scoped', '');
    style.innerHTML = code;
    this.document.body.append(style);
  }
}
